import { useState, useEffect, useCallback } from 'react';
import { CodeEditor } from './CodeEditor/CodeEditor';
import { AlertTriangle, Save } from 'lucide-react';
import { ConfirmationModal } from './ui/confirmation-modal';

interface DocumentEditorProps {
  document: Record<string, unknown>;
  excludeFields?: string[];
  /** Persists the parsed document; the caller owns transport and cache updates. */
  onSave: (data: Record<string, unknown>) => Promise<void>;
}

function fieldType(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

interface TypeChange {
  path: string;
  before: string;
  after: string;
}

const typeChangeAlertStyles: Record<string, React.CSSProperties> = {
  panel: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    border: '1px solid rgba(245, 158, 11, 0.6)',
    background: 'rgba(245, 158, 11, 0.08)',
    borderRadius: '0.375rem',
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#f59e0b',
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '0.375rem',
    marginTop: '0.5rem',
    fontSize: '0.8rem',
  },
  field: {
    fontFamily: 'monospace',
    background: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '0.25rem',
    padding: '0.125rem 0.375rem',
    wordBreak: 'break-word',
  },
  beforeType: {
    fontFamily: 'monospace',
    background: 'rgba(239, 68, 68, 0.15)',
    color: '#f87171',
    borderRadius: '0.25rem',
    padding: '0.125rem 0.375rem',
    textDecoration: 'line-through',
  },
  afterType: {
    fontFamily: 'monospace',
    background: 'rgba(16, 185, 129, 0.15)',
    color: '#34d399',
    borderRadius: '0.25rem',
    padding: '0.125rem 0.375rem',
  },
};

/**
 * Lists fields whose type differs between the stored document and the edited
 * one (e.g. a number accidentally quoted into a string). Added and removed
 * fields are not type drift, so only paths present on both sides compare.
 */
function collectTypeChanges(
  before: unknown,
  after: unknown,
  path = '',
): TypeChange[] {
  const beforeType = fieldType(before);
  const afterType = fieldType(after);
  if (beforeType !== afterType) {
    return [{ path: path || 'document', before: beforeType, after: afterType }];
  }
  const changes: TypeChange[] = [];
  if (beforeType === 'object') {
    const beforeMap = before as Record<string, unknown>;
    const afterMap = after as Record<string, unknown>;
    for (const key of Object.keys(beforeMap)) {
      if (key in afterMap) {
        changes.push(
          ...collectTypeChanges(
            beforeMap[key],
            afterMap[key],
            path ? `${path}.${key}` : key,
          ),
        );
      }
    }
  } else if (beforeType === 'array') {
    const beforeItems = before as unknown[];
    const afterItems = after as unknown[];
    beforeItems.forEach((item, index) => {
      if (index < afterItems.length) {
        changes.push(
          ...collectTypeChanges(item, afterItems[index], `${path}[${index}]`),
        );
      }
    });
  }
  return changes;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({
  document,
  // The document ID and the _updateTime concurrency token are transport
  // metadata, not editable fields.
  excludeFields = ['id', '_updateTime'],
  onSave,
}) => {
  const [editorContent, setEditorContent] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState<boolean>(false);
  const [typeChanges, setTypeChanges] = useState<TypeChange[]>([]);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  useEffect(() => {
    // Prepare document data for editing, excluding specified fields
    if (!initialLoad) return; // Skip after initial load
    
    const editableDocument = { ...document };
    excludeFields.forEach(field => {
      delete editableDocument[field];
    });

    try {
      setEditorContent(JSON.stringify(editableDocument, null, 2));
      setParseError(null);
      setInitialLoad(false);
    } catch (error) {
      console.error('Error preparing document for editing:', error);
      setEditorContent('{}');
      setParseError('Error preparing document: ' + (error as Error).message);
      setInitialLoad(false);
    }
  }, [document, excludeFields, initialLoad]);

  // Use a stable callback for the onChange handler
  const handleEditorChange = useCallback((value: string) => {
    setEditorContent(value);
  }, []);

  // Format the code when requested
  const handleFormat = useCallback((formattedValue: string) => {
    setEditorContent(formattedValue);
  }, []);

  const handleSaveClick = () => {
    // Validate before showing confirm dialog
    let parsed: unknown;
    try {
      parsed = JSON.parse(editorContent);
      setParseError(null);
    } catch (error) {
      setParseError((error as Error).message);
      return;
    }

    const editableDocument = { ...document };
    excludeFields.forEach(field => {
      delete editableDocument[field];
    });
    setTypeChanges(collectTypeChanges(editableDocument, parsed));
    setShowSaveConfirm(true);
  };

  const handleCancelSave = () => {
    setShowSaveConfirm(false);
  };

  const handleConfirmSave = async () => {
    setShowSaveConfirm(false);
    try {
      // No need to validate again since we already did before showing the confirm dialog
      setIsSaving(true);
      const updatedDocument = JSON.parse(editorContent);

      // If the document has a schema field that's a string, preserve it as a string
      if (document.schema && typeof document.schema === 'string' && updatedDocument.schema) {
        updatedDocument.schema = String(updatedDocument.schema);
      }

      await onSave(updatedDocument);
    } catch (error) {
      console.error('Error saving document:', error);
      setParseError(`Save failed: ${(error as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="component-header">
        <h2>Document Editor</h2>
        <div className="component-controls">
          <button 
            onClick={handleSaveClick} 
            disabled={isSaving}
            className={`button-primary ${isSaving ? 'saving' : ''}`}
          >
            <Save size={16} />
            {isSaving ? 'Saving...' : 'Save Document'}
          </button>
        </div>
      </div>

      {parseError && (
        <div className="button-danger subtle">
          <p>Error: {parseError}</p>
        </div>
      )}
      
      <div style={{ height: '500px', marginBottom: '1rem' }}>
        <CodeEditor
          height="500px"
          value={editorContent}
          onChange={handleEditorChange}
          readOnly={false}
          showFormatButton={true}
          onFormat={handleFormat}
          language="json"
        />
      </div>

      {showSaveConfirm && (
        <ConfirmationModal
          isOpen={showSaveConfirm}
          onClose={handleCancelSave}
          onConfirm={handleConfirmSave}
          title="Confirm Save"
          message="Are you sure you want to save changes to this document?"
          confirmLabel="Save Document"
          confirmIcon={<Save size={16} />}
          type={typeChanges.length > 0 ? 'warning' : 'info'}
        >
          {typeChanges.length > 0 && (
            <div style={typeChangeAlertStyles.panel} role="alert">
              <div style={typeChangeAlertStyles.heading}>
                <AlertTriangle size={16} />
                Field types will change
              </div>
              {typeChanges.map((change) => (
                <div key={change.path} style={typeChangeAlertStyles.row}>
                  <code style={typeChangeAlertStyles.field}>{change.path}</code>
                  <span style={typeChangeAlertStyles.beforeType}>
                    {change.before}
                  </span>
                  <span aria-hidden="true">→</span>
                  <span style={typeChangeAlertStyles.afterType}>
                    {change.after}
                  </span>
                </div>
              ))}
            </div>
          )}
        </ConfirmationModal>
      )}
    </div>
  );
};

export default DocumentEditor; 